import { apiHelper } from "../../utils/apiHelper/apiHelper";
import { toast } from "react-toastify";
import {
  LAW_LOADER,
  SET_LAW_TYPES_DATA,
  SET_LAW_PAGE_DATA,
  DOWNLOAD_COUNT,
  SET_SINGLE_LAW_TYPE,
  FETCH_CATEGORIES,
  DOWNLOAD_LAW_TYPE_COUNT,
  FETCH_COUNTRY_ALBUM,
} from "../actionTypes";

/**
 * Fetches the law page data.
 *
 * @function getLawPageData
 * @returns {Promise<void>} A promise that resolves to void.
 */
export const getLawPageData = () => async (dispatch) => {
  try {
    dispatch({
      type: LAW_LOADER,
      payload: true,
    });

    const res = await apiHelper("get", "/cms/law");
    if (res?.data) {
      dispatch({
        type: SET_LAW_PAGE_DATA,
        payload: res?.data?.data,
      });
    }
    dispatch({
      type: LAW_LOADER,
      payload: false,
    });
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LAW_LOADER,
      payload: false,
    });
  }
};

/**
 * Fetches the law types data.
 *
 * @function getLawTypesData
 * @param {number} [page=1] - The page number to fetch.
 * @param {number} [limit=9] - The number of items to fetch per page.
 * @returns {Promise<void>} A promise that resolves to void.
 */
export const getLawTypesData =
  (page = 1, limit = 9) =>
  async (dispatch) => {
    try {
      dispatch({
        type: LAW_LOADER,
        payload: true,
      });

      const res = await apiHelper(
        "get",
        `/cms/law-type?page=${page}&limit=${limit}`,
      );
      if (res?.data) {
        dispatch({
          type: SET_LAW_TYPES_DATA,
          payload: res?.data?.data,
        });
      }
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    }
  };

/**
 * Fetches categories with pagination.
 *
 * Dispatches a loading action before making an API call to retrieve
 * the list of categories. Once the data is successfully fetched, it
 * dispatches an action to store the categories in the state and
 * stops the loading state. If an error occurs during the API call,
 * it displays an error message and stops the loading state.
 *
 * @param {number} page - The page number for pagination.
 * @param {number} limit - The number of categories to fetch per page.
 * @param {string} queryParams - The page tag to filter categories by.
 * @returns {Function} A thunk function that performs the asynchronous operation.
 */
export const fetchCategories =
  (page, limit, pageTag, title) => async (dispatch) => {
    try {
      dispatch({
        type: LAW_LOADER,
        payload: true,
      });
      const res = await apiHelper(
        "get",
        `/categories?page=${page}&limit=${limit}&pageTag=${pageTag}&title=${title}`,
      );
      if (res?.data) {
        dispatch({
          type: FETCH_CATEGORIES,
          payload: res?.data?.data,
        });
        dispatch({
          type: LAW_LOADER,
          payload: false,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    }
  };

/**
 * Asynchronously fetches a single law category by its ID, with optional pagination and filtering.
 *
 * @param {string} id - The ID of the law category to retrieve.
 * @param {number} [page=1] - The page number for pagination, defaults to 1.
 * @param {Object} [filters={}] - Optional filters for the request, such as query parameters.
 * @returns {Function} A dispatch function to manage Redux state updates.
 *
 * @async
 * @function
 * @throws {Error} If the request fails, an error message is displayed and loading state is updated.
 */

export const getSingleLawCategory =
  (id, page = 1, filters = {}) =>
  async (dispatch) => {
    try {
      dispatch({
        type: LAW_LOADER,
        payload: true,
      });
      const queryParams = new URLSearchParams({
        ...filters,
        page,
        limit: 6,
      }).toString();
      const res = await apiHelper(
        "get",
        `/categories/law/${id}?${queryParams}`,
      );
      if (res?.data) {
        dispatch({
          type: SET_SINGLE_LAW_TYPE,
          payload: res?.data?.data,
        });
      }
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    }
  };
/**
 * Asynchronously fetches a single law type by its ID.
 *
 * @param {string} id - The ID of the law type to retrieve.
 * @returns {Function} A dispatch function to manage Redux state updates.
 *
 * @async
 * @function
 * @throws {Error} If the request fails, an error message is displayed and loading state is updated.
 */

export const getSingleLawType = (id) => async (dispatch) => {
  try {
    dispatch({
      type: LAW_LOADER,
      payload: true,
    });

    const res = await apiHelper("get", `/cms/law-type/${id}`);
    if (res?.data) {
      dispatch({
        type: SET_SINGLE_LAW_TYPE,
        payload: res?.data?.data,
      });
    }
    dispatch({
      type: LAW_LOADER,
      payload: false,
    });
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LAW_LOADER,
      payload: false,
    });
  }
};

/**
 * Asynchronously updates the download count of a law by its ID.
 *
 * @param {number} id - The ID of the law whose download count is to be updated.
 * @param {number} param - The number to be added to the download count.
 * @returns {Function} A dispatch function to manage Redux state updates.
 *
 * @async
 * @function
 * @throws {Error} Throws an error if the request fails, and displays an error message.
 */
export const updateDownloadCount = (id, param) => async (dispatch) => {
  try {
    dispatch({
      type: LAW_LOADER,
      payload: true,
    });

    const res = await apiHelper(
      "get",
      `/cms/law/download/${id}?countUpdate=${param}`,
    );
    if (res?.data) {
      dispatch({
        type: DOWNLOAD_COUNT,
        payload: param,
      });
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LAW_LOADER,
      payload: false,
    });
  }
};

/**
 * Asynchronously updates the download count of a law type by its ID.
 * Dispatches LAW_LOADER with true before making an API call to update
 * the download count. On success, dispatches DOWNLOAD_LAW_TYPE_COUNT
 * and sets LAW_LOADER to false. In case of an error, displays an error
 * toast and sets LAW_LOADER to false.
 *
 * @param {number} id - The ID of the law type whose download count is to be updated.
 * @param {number} param - The number to be added to the download count.
 * @returns {Function} A dispatch function to manage Redux state updates.
 *
 * @async
 * @function
 * @throws {Error} Throws an error if the request fails, and displays an error message.
 */

export const updateLawTpeDownloadCount = (id, param) => async (dispatch) => {
  try {
    dispatch({
      type: LAW_LOADER,
      payload: true,
    });

    const res = await apiHelper(
      "get",
      `/cms/law-type/download/${id}?countUpdate=${param}`,
    );
    if (res?.data) {
      dispatch({
        type: DOWNLOAD_LAW_TYPE_COUNT,
        payload: id,
      });
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LAW_LOADER,
      payload: false,
    });
  }
};

export const fetchCountryList =
  (page, limit, queryParams) => async (dispatch) => {
    try {
      dispatch({
        type: LAW_LOADER,
        payload: true,
      });
      const res = await apiHelper(
        "get",
        `/cms/country-album/list?page=${page}&limit=${limit}&${queryParams}`,
      );
      if (res?.data) {
        dispatch({
          type: FETCH_COUNTRY_ALBUM,
          payload: res.data?.data,
        });
        dispatch({
          type: LAW_LOADER,
          payload: false,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      dispatch({
        type: LAW_LOADER,
        payload: false,
      });
    }
  };
